export type ProjectStatus = 'Active' | 'Planning' | 'On Hold' | 'Complete';
export type StageStatus = 'completed' | 'active' | 'upcoming' | 'delayed';

export interface Stage {
  id: string;
  name: string;
  status: StageStatus;
  progress: number;
  startDate: string;
  endDate: string;
  description: string;
  photoCount: number;
  updateCount: number;
}

export interface Project {
  id: string;
  name: string;
  address: string;
  suburb: string;
  status: ProjectStatus;
  currentStage: string;
  progress: number;
  clientName: string;
  startDate: string;
  estCompletion: string;
  contractValue: string;
  contractValueRaw: number;
  pendingApprovals: number;
  daysRemaining: number;
  description: string;
  consentNumber: string;
  stages: Stage[];
  coverImage: string;
  lastUpdate: string;
  lastUpdateTime: string;
  waitingOn: 'Subcontractor' | 'Client Approval' | 'Council' | null;
}

export const mockProjects: Project[] = [
  {
    id: 'JH-2024-001',
    name: 'Henderson Renovation',
    address: '42 Bethells Rd, Henderson',
    suburb: 'Henderson, Auckland 0612',
    status: 'Active',
    currentStage: 'Fit-out',
    progress: 68,
    clientName: 'Yamamoto Family',
    startDate: '8 Jan 2024',
    estCompletion: '14 Jun 2024',
    contractValue: '$148,500',
    contractValueRaw: 148500,
    pendingApprovals: 2,
    daysRemaining: 52,
    description: 'Full interior renovation including kitchen, two bathrooms, and living areas. Weatherboard replacement on north elevation. New deck and landscaping.',
    consentNumber: 'BC-2024-00187',
    lastUpdate: 'Kitchen joinery installed — benchtop templating complete. Tiler on site Wednesday.',
    lastUpdateTime: '18 min ago',
    waitingOn: 'Client Approval',
    coverImage: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20renovation%20in%20progress%20New%20Zealand%20home%20interior%20construction%20workers%20installing%20cabinetry%20warm%20natural%20light%20professional%20photography&width=800&height=500&seq=proj001&orientation=landscape',
    stages: [
      { id: 's1', name: 'Demolition', status: 'completed', progress: 100, startDate: '8 Jan', endDate: '19 Jan', description: 'Strip out existing kitchen, bathrooms, and internal linings', photoCount: 24, updateCount: 8 },
      { id: 's2', name: 'Structure', status: 'completed', progress: 100, startDate: '22 Jan', endDate: '9 Feb', description: 'New structural framing, LVL beams, and roof tie-ins', photoCount: 31, updateCount: 12 },
      { id: 's3', name: 'Rough-in', status: 'completed', progress: 100, startDate: '12 Feb', endDate: '1 Mar', description: 'Plumbing, electrical, and HVAC rough-in. Council inspection passed.', photoCount: 18, updateCount: 9 },
      { id: 's4', name: 'Fit-out', status: 'active', progress: 72, startDate: '4 Mar', endDate: '26 Apr', description: 'Kitchen joinery, bathroom tiling, internal linings, and joinery installation', photoCount: 42, updateCount: 17 },
      { id: 's5', name: 'Finishing', status: 'upcoming', progress: 0, startDate: '29 Apr', endDate: '24 May', description: 'Painting, flooring, fixtures, and final fit-off', photoCount: 0, updateCount: 0 },
      { id: 's6', name: 'Handover', status: 'upcoming', progress: 0, startDate: '27 May', endDate: '14 Jun', description: 'CCC inspection, defects list, practical completion, and client handover', photoCount: 0, updateCount: 0 },
    ],
  },
  {
    id: 'JH-2024-002',
    name: 'Remuera Full Build',
    address: '18 Arney Rd, Remuera',
    suburb: 'Remuera, Auckland 1050',
    status: 'Active',
    currentStage: 'Rough-in',
    progress: 34,
    clientName: 'Chen Family',
    startDate: '15 Feb 2024',
    estCompletion: '20 Dec 2024',
    contractValue: '$395,000',
    contractValueRaw: 395000,
    pendingApprovals: 3,
    daysRemaining: 241,
    description: 'New 4-bedroom family home on cleared section. High-spec finishes, double garage, pool, and landscaped gardens. Structural steel frame.',
    consentNumber: 'BC-2024-00412',
    lastUpdate: 'Plumbing rough-in inspection passed — all wet areas signed off. CCC pathway on track.',
    lastUpdateTime: '1 hr ago',
    waitingOn: 'Subcontractor',
    coverImage: 'https://readdy.ai/api/search-image?query=new%20home%20construction%20site%20New%20Zealand%20timber%20framing%20structure%20blue%20sky%20residential%20build%20professional%20photography%20clean%20background&width=800&height=500&seq=proj002&orientation=landscape',
    stages: [
      { id: 's1', name: 'Demolition', status: 'completed', progress: 100, startDate: '15 Feb', endDate: '22 Feb', description: 'Site clearance and existing structure removal', photoCount: 12, updateCount: 4 },
      { id: 's2', name: 'Structure', status: 'completed', progress: 100, startDate: '25 Feb', endDate: '22 Mar', description: 'Foundations, slab, and structural steel frame erected', photoCount: 38, updateCount: 14 },
      { id: 's3', name: 'Rough-in', status: 'active', progress: 60, startDate: '25 Mar', endDate: '19 Apr', description: 'Plumbing, electrical, and HVAC rough-in across all levels', photoCount: 22, updateCount: 8 },
      { id: 's4', name: 'Fit-out', status: 'upcoming', progress: 0, startDate: '22 Apr', endDate: '14 Jun', description: 'Internal linings, joinery, kitchen, and bathroom fit-out', photoCount: 0, updateCount: 0 },
      { id: 's5', name: 'Finishing', status: 'upcoming', progress: 0, startDate: '17 Jun', endDate: '22 Nov', description: 'Painting, flooring, landscaping, pool, and external works', photoCount: 0, updateCount: 0 },
      { id: 's6', name: 'Handover', status: 'upcoming', progress: 0, startDate: '25 Nov', endDate: '20 Dec', description: 'CCC inspection, defects, practical completion', photoCount: 0, updateCount: 0 },
    ],
  },
  {
    id: 'JH-2024-003',
    name: 'Ponsonby Extension',
    address: '3 Franklin Rd, Ponsonby',
    suburb: 'Ponsonby, Auckland 1011',
    status: 'On Hold',
    currentStage: 'Structure',
    progress: 52,
    clientName: 'Tanaka Family',
    startDate: '5 Nov 2023',
    estCompletion: '30 Aug 2024',
    contractValue: '$82,000',
    contractValueRaw: 82000,
    pendingApprovals: 1,
    daysRemaining: 129,
    description: 'Rear extension adding open-plan kitchen/dining and master bedroom suite. On hold pending council approval for boundary setback variation.',
    consentNumber: 'BC-2023-01854',
    lastUpdate: 'On hold — awaiting council boundary setback variation approval.',
    lastUpdateTime: '2 days ago',
    waitingOn: 'Council',
    coverImage: 'https://readdy.ai/api/search-image?query=residential%20home%20extension%20construction%20New%20Zealand%20rear%20addition%20timber%20frame%20on%20hold%20site%20professional%20photography%20overcast%20sky&width=800&height=500&seq=proj003&orientation=landscape',
    stages: [
      { id: 's1', name: 'Demolition', status: 'completed', progress: 100, startDate: '5 Nov', endDate: '10 Nov', description: 'Rear wall removal and site preparation', photoCount: 8, updateCount: 3 },
      { id: 's2', name: 'Structure', status: 'delayed', progress: 52, startDate: '13 Nov', endDate: 'TBC', description: 'Foundation and framing — paused pending council variation', photoCount: 16, updateCount: 6 },
      { id: 's3', name: 'Rough-in', status: 'upcoming', progress: 0, startDate: 'TBC', endDate: 'TBC', description: 'Plumbing and electrical rough-in', photoCount: 0, updateCount: 0 },
      { id: 's4', name: 'Fit-out', status: 'upcoming', progress: 0, startDate: 'TBC', endDate: 'TBC', description: 'Kitchen, bathroom, and internal fit-out', photoCount: 0, updateCount: 0 },
      { id: 's5', name: 'Finishing', status: 'upcoming', progress: 0, startDate: 'TBC', endDate: 'TBC', description: 'Painting, flooring, and external cladding', photoCount: 0, updateCount: 0 },
      { id: 's6', name: 'Handover', status: 'upcoming', progress: 0, startDate: 'TBC', endDate: '30 Aug', description: 'CCC and practical completion', photoCount: 0, updateCount: 0 },
    ],
  },
  {
    id: 'JH-2024-004',
    name: 'Mt Eden Kitchen',
    address: '7 Domain Rd, Mt Eden',
    suburb: 'Mt Eden, Auckland 1024',
    status: 'Planning',
    currentStage: 'Design & Consent',
    progress: 8,
    clientName: 'Watanabe Family',
    startDate: '1 May 2024',
    estCompletion: '15 Aug 2024',
    contractValue: '$54,000',
    contractValueRaw: 54000,
    pendingApprovals: 1,
    daysRemaining: 114,
    description: 'Full kitchen renovation including new joinery, benchtops, appliances, and splashback. Consent application in preparation.',
    consentNumber: 'Pending',
    lastUpdate: 'Consent drawings submitted to Auckland Council.',
    lastUpdateTime: '3 days ago',
    waitingOn: 'Council',
    coverImage: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20design%20planning%20New%20Zealand%20home%20renovation%20blueprints%20samples%20materials%20selection%20professional%20photography%20clean%20white%20background&width=800&height=500&seq=proj004&orientation=landscape',
    stages: [
      { id: 's1', name: 'Design & Consent', status: 'active', progress: 80, startDate: '1 Apr', endDate: '30 Apr', description: 'Final design, consent drawings, and council submission', photoCount: 4, updateCount: 3 },
      { id: 's2', name: 'Demolition', status: 'upcoming', progress: 0, startDate: '1 May', endDate: '3 May', description: 'Strip existing kitchen', photoCount: 0, updateCount: 0 },
      { id: 's3', name: 'Rough-in', status: 'upcoming', progress: 0, startDate: '6 May', endDate: '17 May', description: 'Plumbing and electrical modifications', photoCount: 0, updateCount: 0 },
      { id: 's4', name: 'Fit-out', status: 'upcoming', progress: 0, startDate: '20 May', endDate: '28 Jun', description: 'New joinery, benchtops, and appliances', photoCount: 0, updateCount: 0 },
      { id: 's5', name: 'Finishing', status: 'upcoming', progress: 0, startDate: '1 Jul', endDate: '26 Jul', description: 'Splashback, painting, and final fit-off', photoCount: 0, updateCount: 0 },
      { id: 's6', name: 'Handover', status: 'upcoming', progress: 0, startDate: '29 Jul', endDate: '15 Aug', description: 'Inspection and practical completion', photoCount: 0, updateCount: 0 },
    ],
  },
  {
    id: 'JH-2023-021',
    name: 'Epsom Bathroom',
    address: '22 Gillies Ave, Epsom',
    suburb: 'Epsom, Auckland 1023',
    status: 'Complete',
    currentStage: 'Handover',
    progress: 100,
    clientName: 'Nakamura Family',
    startDate: '10 Oct 2023',
    estCompletion: '15 Dec 2023',
    contractValue: '$38,000',
    contractValueRaw: 38000,
    pendingApprovals: 0,
    daysRemaining: 0,
    description: 'Full bathroom renovation — new wet area, vanity, heated towel rail, and tiling. Completed on time and within budget.',
    consentNumber: 'BC-2023-01102',
    lastUpdate: 'Practical completion signed off — project complete.',
    lastUpdateTime: '4 months ago',
    waitingOn: null,
    coverImage: 'https://readdy.ai/api/search-image?query=completed%20luxury%20bathroom%20renovation%20New%20Zealand%20home%20white%20tiles%20modern%20vanity%20heated%20towel%20rail%20professional%20photography%20clean%20bright&width=800&height=500&seq=proj005&orientation=landscape',
    stages: [
      { id: 's1', name: 'Demolition', status: 'completed', progress: 100, startDate: '10 Oct', endDate: '13 Oct', description: 'Strip existing bathroom', photoCount: 6, updateCount: 2 },
      { id: 's2', name: 'Rough-in', status: 'completed', progress: 100, startDate: '16 Oct', endDate: '27 Oct', description: 'Plumbing and electrical rough-in', photoCount: 9, updateCount: 4 },
      { id: 's3', name: 'Fit-out', status: 'completed', progress: 100, startDate: '30 Oct', endDate: '24 Nov', description: 'Tiling, vanity, shower, and fixtures', photoCount: 22, updateCount: 8 },
      { id: 's4', name: 'Finishing', status: 'completed', progress: 100, startDate: '27 Nov', endDate: '8 Dec', description: 'Painting, accessories, and final fit-off', photoCount: 11, updateCount: 4 },
      { id: 's5', name: 'Handover', status: 'completed', progress: 100, startDate: '11 Dec', endDate: '15 Dec', description: 'Inspection and practical completion', photoCount: 5, updateCount: 2 },
    ],
  },
];
